import { ROUTES } from '@/constants'
import type { LANGUAGE_KEY } from '@/context/Locale/Locale'

import { DiffEngine, DiffNode } from './diffEngine'
import type { DecisionKind, MergeRequestRecord, MergeRequestStatus, MrStructure, StructureType } from './types'

export const getMergeRequestPath = (mrId?: string) =>
	mrId ? ROUTES.MERGE_REQUESTS_MR.replace(':mrId', mrId) : ROUTES.MERGE_REQUESTS

export type CreateMergeRequestNavigation = {
	createMergeSourceBranchId: string
	createMergeTargetBranchId: string | null
}

export const MR_STATUS: Record<
	MergeRequestStatus,
	{ labelKey: LANGUAGE_KEY; color: string; bg: string }
> = {
	OPEN: { labelKey: 'MERGE_REQUESTS_STATUS_OPEN', color: '#1F4954', bg: '#E7EDEF' },
	CHANGES_REQUESTED: {
		labelKey: 'MERGE_REQUESTS_STATUS_CHANGES_REQUESTED',
		color: '#C26401',
		bg: '#FEF2DC',
	},
	APPROVED: { labelKey: 'MERGE_REQUESTS_STATUS_APPROVED', color: '#0F9D58', bg: '#E6F4EA' },
	MERGED: { labelKey: 'MERGE_REQUESTS_STATUS_MERGED', color: '#5B43C4', bg: '#EEEAFA' },
	REJECTED: { labelKey: 'MERGE_REQUESTS_STATUS_REJECTED', color: '#C5221F', bg: '#FDE7E6' },
	CLOSED: { labelKey: 'MERGE_REQUESTS_STATUS_CLOSED', color: '#7A7A7A', bg: '#ECECEC' },
	STALE: { labelKey: 'MERGE_REQUESTS_STATUS_STALE', color: '#B0863A', bg: '#FBF3E2' },
}

export const DECISION: Record<DecisionKind, { labelKey: LANGUAGE_KEY; color: string; bg: string }> = {
	APPROVED: { labelKey: 'MERGE_REQUESTS_STATUS_APPROVED', color: '#0F9D58', bg: '#E6F4EA' },
	CHANGES_REQUESTED: {
		labelKey: 'MERGE_REQUESTS_DECISION_CHANGES_REQUESTED',
		color: '#C26401',
		bg: '#FEF2DC',
	},
	REJECTED: { labelKey: 'MERGE_REQUESTS_STATUS_REJECTED', color: '#C5221F', bg: '#FDE7E6' },
}

export const MR_USERS: Record<string, { name: string; initials: string; color: string }> = {
	jmedilek: { name: 'Jiri Medilek', initials: 'JM', color: '#F9423A' },
	mspilar: { name: 'Martin Špilar', initials: 'MŠ', color: '#1F4954' },
	jpatockova: { name: 'Jarmila Patočková', initials: 'JP', color: '#5B43C4' },
	rmasuran: { name: 'R. Masuran', initials: 'RM', color: '#0F9D58' },
}

export const STRUCTURE_TYPE_META: Record<string, { labelKey: LANGUAGE_KEY; color: string }> = {
	table: { labelKey: 'MERGE_REQUESTS_TYPE_TABLE', color: '#1F4954' },
	diagram: { labelKey: 'MERGE_REQUESTS_TYPE_DIAGRAM', color: '#5B43C4' },
	mapping: { labelKey: 'MERGE_REQUESTS_TYPE_MAPPING', color: '#C26401' },
	api: { labelKey: 'MERGE_REQUESTS_TYPE_API', color: '#0F9D58' },
	expectation: { labelKey: 'MERGE_REQUESTS_TYPE_EXPECTATION', color: '#00897B' },
	mapping_read: { labelKey: 'MERGE_REQUESTS_TYPE_MAPPING', color: '#C26401' },
	mapping_write: { labelKey: 'MERGE_REQUESTS_TYPE_MAPPING', color: '#C26401' },
}

export const userOf = (id: string) =>
	MR_USERS[id] || {
		name: id,
		initials: (id || '?').slice(0, 2).toUpperCase(),
		color: '#7A7A7A',
	}

const USER_COLORS = ['#1F4954', '#5B43C4', '#0F9D58', '#C26401', '#F9423A', '#7A7A7A']

export const initialsOf = (name: string) => {
	const parts = name.trim().split(/\s+/).filter(Boolean)
	if (parts.length >= 2) {
		return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
	}
	return (name.trim() || '?').slice(0, 2).toUpperCase()
}

export const userAppearance = (name?: string, id?: string | number | null) => {
	const key = id != null && id !== '' ? String(id) : ''
	const label = name?.trim() || key || '?'
	let hash = 0
	for (let i = 0; i < (key || label).length; i++) hash += (key || label).charCodeAt(i)
	return {
		name: label,
		initials: initialsOf(label),
		color: USER_COLORS[hash % USER_COLORS.length],
	}
}

export const buildTree = (structure: MrStructure): DiffNode => {
	if (!structure.tree) {
		structure.tree = DiffEngine.buildDiffTree(
			structure.data.base,
			structure.data.source,
			structure.data.target,
		)
	}
	return structure.tree as DiffNode
}

export const rebuildTree = (structure: MrStructure): DiffNode => {
	structure.tree = DiffEngine.buildDiffTree(
		structure.data.base,
		structure.data.source,
		structure.data.target,
	)
	return structure.tree as DiffNode
}

const classify = (
	n: DiffNode,
	c: { added: number; removed: number; modified: number; conflict: number },
) => {
	const s = n.status
	if (DiffEngine.CONFLICT_STATUSES.has(s) || s === 'has-conflict') {
		c.conflict++
		return
	}
	if (DiffEngine.ADDED_STATUSES.has(s)) {
		c.added++
		return
	}
	if (DiffEngine.REMOVED_STATUSES.has(s)) {
		c.removed++
		return
	}
	if (s === 'has-changes') {
		n.counts && n.counts.conflict > 0 ? c.conflict++ : c.modified++
		return
	}
	if (s === 'modified-src' || s === 'modified-tgt' || s === 'auto') c.modified++
}

export const countStructure = (tree: DiffNode) => {
	const c = { added: 0, removed: 0, modified: 0, conflict: 0 }
	;(tree.children || []).forEach((child) => {
		if (child.kind === 'array' && child.children?.length) {
			child.children.forEach((it) => classify(it, c))
		} else classify(child, c)
	})
	return c
}

const isConflict = (node: DiffNode, conflictPaths?: string[]) =>
	conflictPaths
		? conflictPaths.includes(node.path)
		: DiffEngine.CONFLICT_STATUSES.has(node.status)

export const unresolvedCount = (tree: DiffNode, conflictPaths?: string[]) =>
	DiffEngine.flatten(tree).filter(
		(node) => isConflict(node, conflictPaths) && !node.resolution,
	).length

export const conflictNodes = (node: DiffNode, conflictPaths?: string[]) =>
	DiffEngine.flatten(node).filter((child) => isConflict(child, conflictPaths))

type AttrRow = { node: DiffNode; type: 'leaf' }

// Nested objects (e.g. historyTable) have no tab of their own, so their changed
// leaves are flattened into the attribute pane under a qualified label.
const nestedAttrRows = (node: DiffNode, prefix: string, out: AttrRow[]) => {
	for (const child of node.children || []) {
		if (child.kind === 'leaf') {
			if (!DiffEngine.CHANGED_STATUSES.has(child.status)) continue
			child.displayLabel = `${prefix}.${child.label}`
			out.push({ node: child, type: 'leaf' })
			continue
		}
		if (child.kind === 'object') {
			nestedAttrRows(child, `${prefix}.${child.label}`, out)
		}
	}
}

export const topAttrRows = (tree: DiffNode) => {
	const rows: AttrRow[] = []
	for (const child of tree.children || []) {
		if (child.kind === 'leaf') {
			if (DiffEngine.CHANGED_STATUSES.has(child.status)) {
				rows.push({ node: child, type: 'leaf' })
			}
			continue
		}
		if (child.kind === 'object') nestedAttrRows(child, child.label, rows)
	}
	return rows
}

export const collections = (tree: DiffNode) =>
	(tree.children || []).filter((c) => c.kind === 'array' && c.status !== 'unchanged')

export const collectionRows = (arrayNode: DiffNode) => {
	const rows: { node: DiffNode; type: 'leaf' | 'block' | 'entity' }[] = []
	;(arrayNode.children || []).forEach((entity) => {
		const s = entity.status
		const add = DiffEngine.ADDED_STATUSES.has(s)
		const rem = DiffEngine.REMOVED_STATUSES.has(s)
		if (entity.kind !== 'leaf') {
			if (add || rem) {
				rows.push({ node: entity, type: 'block' })
				return
			}
			const hasCh = s === 'has-conflict' || s === 'has-changes'
			if (!hasCh) return
			rows.push({ node: entity, type: 'entity' })
			;(entity.children || []).forEach((c) => {
				if (DiffEngine.CHANGED_STATUSES.has(c.status)) rows.push({ node: c, type: 'leaf' })
			})
			return
		}
		if (DiffEngine.CHANGED_STATUSES.has(s)) rows.push({ node: entity, type: 'leaf' })
	})
	return rows
}

// Internal identity that carries no review value once the row is already labelled by it.
const ENTITY_NOISE_KEYS = new Set(['id', 'uuid'])

/** Reference objects (domain, stereotype…) are only worth their code or name here. */
const refLabel = (value: Record<string, unknown>) => {
	const label = value.code ?? value.name
	return label == null || label === '' ? null : String(label)
}

export type EntityField = { label: string; value: string }

/**
 * Added and removed entities arrive as whole objects. Listing their informative fields
 * shows the reviewer what actually appeared, where a key count told them nothing.
 */
export const entityFields = (value: unknown): EntityField[] => {
	if (value == null || typeof value !== 'object' || Array.isArray(value)) return []
	const fields: EntityField[] = []
	for (const [label, raw] of Object.entries(value as Record<string, unknown>)) {
		if (ENTITY_NOISE_KEYS.has(label) || raw == null || raw === '') continue
		if (Array.isArray(raw)) {
			if (raw.length) fields.push({ label, value: `[${raw.length}]` })
			continue
		}
		if (typeof raw === 'object') {
			const ref = refLabel(raw as Record<string, unknown>)
			if (ref) fields.push({ label, value: ref })
			continue
		}
		fields.push({ label, value: String(raw) })
	}
	return fields
}

export const fmtValue = (v: unknown) => {
	if (v === undefined) return '— missing —'
	if (v === null) return 'null'
	if (Array.isArray(v)) return `[${v.length}]`
	if (typeof v === 'object') return `{${Object.keys(v as object).length} fields}`
	if (v === '') return '(empty)'
	return String(v)
}

export const statusFamily = (s: string) => {
	if (s === 'conflict' || s === 'added-conflict' || s === 'has-conflict') return 'conflict'
	if (DiffEngine.ADDED_STATUSES.has(s)) return 'added'
	if (DiffEngine.REMOVED_STATUSES.has(s)) return 'removed'
	return 'modified'
}

export const FAM_COLOR = {
	conflict: '#C5221F',
	added: '#0F9D58',
	removed: '#C5221F',
	modified: '#C26401',
} as const

export const FAM_GLYPH = {
	conflict: '!',
	added: '+',
	removed: '–',
	modified: '~',
} as const

export const inScope = (mr: MergeRequestRecord) => {
	if (mr.scope === 'PARTIAL' && mr.selected) {
		return mr.structures.filter((s) => mr.selected!.includes(s.key))
	}
	return mr.structures
}

const DIFF_SUMMARY_PATTERNS = {
	added: /added\D*(\d+)/i,
	conflict: /conflicts?\D*(\d+)/i,
	modified: /modified\D*(\d+)/i,
	removed: /removed\D*(\d+)/i,
} as const

/** Reads the backend summary string, e.g. "Added 0, removed 0, modified 2, conflicts 1". */
export const parseDiffSummary = (summary?: string | null) => {
	if (!summary) return null
	const counts = { added: 0, conflict: 0, modified: 0, removed: 0 }
	let matched = false
	for (const [key, pattern] of Object.entries(DIFF_SUMMARY_PATTERNS)) {
		const found = pattern.exec(summary)
		if (!found) continue
		matched = true
		counts[key as keyof typeof counts] = Number(found[1])
	}
	return matched ? counts : null
}

export const aggregateMr = (mr: MergeRequestRecord) => {
	// The summary counts the whole merge request, while local trees only see what the
	// `changes` payload carried — often a coarse blob that counts as nothing.
	const fromSummary = parseDiffSummary(mr.diffSummary)

	if (mr.structures.length) {
		const tot = { added: 0, removed: 0, modified: 0, conflict: 0 }
		let unresolved = 0
		inScope(mr).forEach((s) => {
			const tree = buildTree(s)
			const c = countStructure(tree)
			tot.added += c.added
			tot.removed += c.removed
			tot.modified += c.modified
			tot.conflict += c.conflict
			unresolved += unresolvedCount(tree, s.conflictPaths)
		})
		// A cherry-pick merges only the selected structures, so its own totals describe it
		// better than a summary covering every change on the branch.
		const cherryPicked = mr.scope === 'PARTIAL' && Object.values(tot).some(Boolean)
		// Merge gating stays tree-based so a stale summary can never block the merge button.
		return { counts: !fromSummary || cherryPicked ? tot : fromSummary, unresolved }
	}

	if (fromSummary) return { counts: fromSummary, unresolved: 0 }
	const counts = { added: 0, removed: 0, modified: 0, conflict: 0 }
	for (const change of mr.changes ?? []) {
		if (change.changeType === 'ADDED') counts.added += 1
		else if (change.changeType === 'REMOVED') counts.removed += 1
		else if (change.changeType === 'MODIFIED') counts.modified += 1
	}
	return { counts, unresolved: 0 }
}

export const positiveVotes = (mr: MergeRequestRecord) => {
	if (mr.approvals.length) {
		return mr.approvals.filter((a) => a.decision === 'APPROVED').length
	}
	return mr.approvalCount ?? 0
}

export type StructureTypeKey = StructureType
